<script setup>
/**
 * OdooSummaryPanel
 *
 * The real Odoo customer-info sidebar panel (Phase 2), replacing
 * OdooInfoPlaceholder.vue in the registry (see index.js). Per the
 * registry's own documented pattern, this component takes no props -
 * it reaches into the store for the current conversation itself, the
 * same way the existing sidebar panels do.
 *
 * Fetches GET /api/v1/accounts/:account_id/conversations/:id/odoo_summary
 * (docs/architecture/ADR-003-odoo-api-contract.md, Part B) on mount and
 * whenever the open conversation changes, and renders one of four
 * states: loading, error, unlinked (no matching Odoo customer - a
 * normal, common state, not a failure), or linked (the real summary).
 * An account with no Odoo integration configured yet surfaces as the
 * error state with error_type "configuration_error".
 */
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import Skeleton from 'dashboard/components-next/skeleton/Skeleton.vue';
import Icon from 'next/icon/Icon.vue';
import conversationAPI from 'dashboard/api/conversations';

const { t } = useI18n();

const currentChat = useMapGetter('getSelectedChat');
const conversationId = computed(() => currentChat.value?.id);

const isLoading = ref(false);
const summary = ref(null);
const loadError = ref(false);

const fetchSummary = async () => {
  if (!conversationId.value) return;
  isLoading.value = true;
  loadError.value = false;
  try {
    const response = await conversationAPI.getOdooSummary(conversationId.value);
    summary.value = response.data;
  } catch (error) {
    loadError.value = true;
  } finally {
    isLoading.value = false;
  }
};

watch(conversationId, fetchSummary, { immediate: true });

const errorMessageKey = computed(() => {
  const errorType = summary.value?.error_type;
  return errorType
    ? `CONVERSATION_SIDEBAR.ODOO_SUMMARY.ERRORS.${errorType.toUpperCase()}`
    : 'CONVERSATION_SIDEBAR.ODOO_SUMMARY.ERRORS.UNKNOWN_ERROR';
});

const formatDate = value => {
  if (!value) return '';
  return new Date(value).toLocaleDateString();
};
</script>

<template>
  <div class="flex flex-col gap-3">
    <div v-if="isLoading" class="flex flex-col gap-2">
      <Skeleton height="1.25rem" width="60%" />
      <Skeleton height="1rem" />
      <Skeleton height="1rem" width="80%" />
    </div>

    <div
      v-else-if="loadError || summary?.status === 'error'"
      class="flex items-center gap-2 text-sm text-n-slate-11"
    >
      <Icon icon="i-lucide-alert-circle" class="size-4 shrink-0" />
      <span>{{ t(errorMessageKey) }}</span>
    </div>

    <div
      v-else-if="summary?.status === 'unlinked'"
      class="text-sm text-n-slate-11"
    >
      {{ t('CONVERSATION_SIDEBAR.ODOO_SUMMARY.UNLINKED') }}
    </div>

    <template v-else-if="summary?.status === 'linked'">
      <div>
        <p class="text-sm font-medium text-n-slate-12 mb-0">
          {{ summary.partner?.name }}
        </p>
        <p v-if="summary.partner?.email" class="text-xs text-n-slate-11 mb-0">
          {{ summary.partner.email }}
        </p>
        <p v-if="summary.salesperson" class="text-xs text-n-slate-11 mb-0">
          {{ t('CONVERSATION_SIDEBAR.ODOO_SUMMARY.SALESPERSON') }}:
          {{ summary.salesperson.name }}
        </p>
      </div>

      <div v-if="summary.quotations?.length">
        <p class="text-xs font-medium text-n-slate-11 uppercase mb-1">
          {{ t('CONVERSATION_SIDEBAR.ODOO_SUMMARY.QUOTATIONS') }}
        </p>
        <div
          v-for="quotation in summary.quotations"
          :key="quotation.id"
          class="flex justify-between text-sm text-n-slate-12"
        >
          <span>{{ quotation.name }}</span>
          <span>{{ quotation.amount_total }}</span>
        </div>
      </div>

      <div v-if="summary.invoices?.length">
        <p class="text-xs font-medium text-n-slate-11 uppercase mb-1">
          {{ t('CONVERSATION_SIDEBAR.ODOO_SUMMARY.INVOICES') }}
        </p>
        <div
          v-for="invoice in summary.invoices"
          :key="invoice.id"
          class="flex justify-between text-sm text-n-slate-12"
        >
          <span>{{ invoice.name }}</span>
          <span>{{ invoice.payment_state }}</span>
        </div>
      </div>

      <div v-if="summary.timeline?.length">
        <p class="text-xs font-medium text-n-slate-11 uppercase mb-1">
          {{ t('CONVERSATION_SIDEBAR.ODOO_SUMMARY.TIMELINE') }}
        </p>
        <div
          v-for="event in summary.timeline"
          :key="`${event.type}-${event.ref_id}`"
          class="flex justify-between text-xs text-n-slate-11"
        >
          <span>{{ event.title }}</span>
          <span>{{ formatDate(event.date) }}</span>
        </div>
      </div>
    </template>
  </div>
</template>
